import { defineStore } from "pinia";
import { isAxiosError } from "axios";
import { api } from "@/api/base";

export type AuthStatus = "" | "loading" | "pendingOtp" | "success" | "error";

export type AuthState = {
  sessionId: string;
  status: AuthStatus;
};

const endpoints: { [key: string]: string } = {
  password: "auth/login",
  google: "auth/google/login",
  facebook: "auth/facebook/login",
};

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    // Initialize state from localStorage, just like the Vuex module
    sessionId: localStorage.getItem("session-id") || "",
    status: "",
  }),

  getters: {
    isAuthenticated: (state): boolean => {
      return !!state.sessionId;
    },
  },

  actions: {
    /**
     * Request object accepts the same parameters as the /v1/auth/login
     * API endpoint.
     * The returned object has these attributes:
     * sessionId: The authenticated session ID string returned by the server.
     * otpRequired: Whether OTP authentication must occur before the session
     * becomes valid.
     */
    async login(request: { [key: string]: any }) {
      this.status = "loading";

      if (!request || !request.authMethod) {
        throw new Error("Missing required request data");
      }

      const loginEndpoint = endpoints[request.authMethod];
      if (!loginEndpoint) {
        throw new Error("Invalid authentication method");
      }

      const { authMethod, ...params } = request;

      try {
        const response = await api.post(loginEndpoint, params);

        const sessionId = response.data.response.sessionId;
        localStorage.setItem("session-id", sessionId);
        // Set auth header defaults for api requests
        api.defaults.headers.common["Authorization"] = "Bearer " + sessionId;
        this.sessionId = sessionId;
        if (response.data.response.otpEnabled) {
          this.status = "pendingOtp";
          return {
            sessionId: sessionId,
            userDisabled: response.data.response.userDisabled,
            otpRequired: true,
          };
        } else {
          this.status = "success";
          return {
            sessionId: sessionId,
            userDisabled: response.data.response.userDisabled,
            otpRequired: false,
          };
        }
      } catch (error) {
        this.status = "error";
        localStorage.removeItem("session-id");
        throw error;
      }
    },

    async otp(request: { [key: string]: any }) {
      try {
        await api.post("/auth/otp", request);
        this.status = "success";
        this.sessionId = this.sessionId || "";

        return "loginSuccess";
      } catch (error) {
        this.status = "error";
        localStorage.removeItem("session-id");
        throw error;
      }
    },

    async logout() {
      if (this.sessionId) {
        try {
          await api.post("/auth/logout");
        } catch (error) {
          // 401 (session expired) and network errors (tab closing, connectivity loss)
          // are expected and not actionable — use warn to avoid Sentry noise
          if (isAxiosError(error) && (error.response?.status === 401 || !error.response)) {
            console.warn("Failed to logout from server, logging out locally");
          } else {
            console.error("Failed to logout from server, logging out locally:", error);
          }
          // We continue anyway to ensure client-side logout
        }
      }
      this.sessionId = "";
      localStorage.removeItem("session-id");
      delete api.defaults.headers.common["Authorization"];
    },

    loginSuccess(sessionId: string) {
      this.status = "success";
      this.sessionId = this.sessionId || sessionId;
    },
  },
});
